// https://blog.logrocket.com/how-to-build-an-svg-circular-progress-component-using-react-and-react-hooks/

import { css, cx } from "@emotion/css";
import { useEffect, useRef, useState } from "react";

import { grey, yellow } from "@/modules/theme/utils/colors";

type ProgressCircleProps = {
  size: number;
  progress: number;
  strokeWidth: number;
  progressStroke?: string;
  backgroundStroke?: string;
  children?: React.ReactNode;
  lineCap?: "butt" | "round" | "square";
  className?: string;
  childrenClassName?: string;
};

export const ProgressCircle: React.FC<ProgressCircleProps> = ({
  size,
  progress,
  strokeWidth,
  backgroundStroke = grey[300],
  progressStroke = yellow[400],
  lineCap = "round",
  className,
  childrenClassName,
  children,
}) => {
  const classes = createProgressCircleStyles(size);
  const center = size / 2;
  const radius = size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;

  const [offset, setOffset] = useState(0);
  const circleRef = useRef<SVGCircleElement | null>(null);

  useEffect(() => {
    const progressOffset = ((100 - progress) / 100) * circumference;
    setOffset(progressOffset);
  }, [circumference, progress]);

  return (
    <div className={cx(classes.root, className)}>
      <svg className={classes.svg} width={size} height={size}>
        <circle
          className={classes.circleBackground}
          stroke={backgroundStroke}
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className={classes.circle}
          stroke={progressStroke}
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          ref={circleRef}
          strokeDashoffset={offset}
          strokeLinecap={lineCap}
        />
      </svg>
      <div className={cx(classes.children, childrenClassName)}>{children}</div>
    </div>
  );
};

function createProgressCircleStyles(size: number) {
  return {
    root: css({
      position: "relative",
      width: size,
    }),
    children: css({
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    }),
    svg: css({
      display: "block",
      maxWidth: "100%",
      transform: "rotate(55deg)",
    }),
    circleBackground: css({
      fill: "none",
    }),
    circle: css({
      fill: "none",
    }),
  };
}
